import { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { HeartIcon } from '@radix-ui/react-icons';
import { PageHeader, Button, Alert, AlertTitle, AlertDescription, EmptyState, Card, CardContent, Badge, PostLink } from '../components/ui';
import { EvidenceDrawer, FilterBar, StandingCard } from '../components/composite';
import { useHeartbeatPage, AccountPicker, AccountIdentity, ListeningState, RunHistory, PulseTimeline, MoodTiles, StanceStrip, FlaggedLane, Reply, replyFilters, replySortOptions } from '../components/features/heartbeat';
import { PATHS, ROUTE_PATTERNS, buildRoute } from '../constants/Paths';
import { HeartbeatNotesDialog, ContextReviewBanner, NoteSummary } from '../components/features/heartbeat/HeartbeatContext';
import HeartbeatNavigation from '../components/features/heartbeat/HeartbeatNavigation';
import { reviewIsActive, useHeartbeatContext } from '../components/features/heartbeat/useHeartbeatContext';

// Backend error strings are sentence fragments with no terminal punctuation, and
// the failure alert appends a second sentence after one. Without this the page
// renders "collection failed Try again in a few minutes." as a single run-on.
function endSentence(text) {
	const s = String(text || '').trim();
	if (!s) return '';
	return /[.!?…:]$/.test(s) ? s : `${s}.`;
}

/** Maps a take-pulse failure to a sentence a customer can act on. The most
 * likely failure is a billing refusal (a raw axios error whose .message is
 * literally "Request failed with status code 402") - never show that. The
 * full error still goes to the console for support/debugging. */
function describeTakeError(error) {
	if (!error) return null;
	// eslint-disable-next-line no-console
	console.error('Heartbeat: failed to start a pulse', error);
	if (error.response?.status === 402) {
		return "This workspace's plan doesn't cover another pulse right now. Check billing, then try again.";
	}
	return "We couldn't start this pulse. Please try again in a few minutes.";
}

export default function HeartbeatPage() {
	const { accountId } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const page = useHeartbeatPage(accountId);
	const [depth, setDepth] = useState(10);
	const [contextOpen, setContextOpen] = useState(false);
	const openContext = () => { page.closeVoices(); setContextOpen(true); };
	const sort = searchParams.get('sort') || 'recent';
	// Functional form: building from the captured `searchParams` lets a write
	// with no render between it and this one (a FilterBar chip in the same
	// tick) be discarded. Ordinary sequential clicks re-render in between.
	const setSort = (v) => setSearchParams((prev) => {
		const next = new URLSearchParams(prev);
		next.set('sort', v);
		return next;
	}, { replace: true });
	const takeErrorMessage = useMemo(() => describeTakeError(page.takeError), [page.takeError]);

	// The drawer's title states the count of replies it actually loaded (not
	// the grid cell's own number) so a mismatch between what a cell claimed
	// and what the chips + emotion narrowed it down to is visible, not silent.
	const voicesTitle = page.voices
		? `${page.voices.title || 'Replies'} · ${page.replies.length}${page.hasMoreReplies ? '+' : ''}`
		: 'Replies';

	// Shared between the listening state and the terminal states below it: a
	// previous run's unread replies must stay retryable while a newer run is
	// still in flight, not just after it finishes. `activeRunId` disables the
	// retry control for whichever run is currently live.
	const history = (
		<div>
			<div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">History <Badge variant="outline" size="sm">{page.runs.length}</Badge></div>
			<RunHistory runs={page.runs} onRetry={page.retryLabeling} retrying={page.retrying} activeRunId={page.state === 'listening' ? page.latestRun?._id : null} accountId={accountId} />
		</div>
	);

	const takeButton = (label = 'Take the pulse') => (
		<div className="flex items-center gap-2">
			<select value={depth} onChange={(e) => setDepth(Number(e.target.value))} className="rounded-lg border border-border px-2 py-2 text-sm" aria-label="How many recent posts">
				<option value={10}>Last 10 posts · quick</option><option value={20}>Last 20 posts · deeper</option>
			</select>
			<Button onClick={() => page.takePulse(depth)} disabled={page.taking || page.state === 'listening'}>{page.taking ? 'Starting…' : label}</Button>
		</div>
	);

	return (
		// Page gutters and width cap live on the page, not the layout:
		// AuthenticatedLayout renders children into a bare scroll container, so a
		// page without its own padding sits flush against the sidebar and the
		// window edge. `p-4 sm:p-8` matches DashboardHeader's own `px-4 sm:px-8`,
		// which is what lines the page title up under the header's section label.
		<main className="p-4 sm:p-8 max-w-7xl mx-auto">
			<PageHeader title="Heartbeat" description="How the internet feels about an account, one pulse at a time." actions={accountId && page.state !== 'no-account' ? takeButton() : null} />
			<HeartbeatNavigation />
			{accountId ? (
				<AccountIdentity
					account={page.account}
					isLoading={page.accountLoading}
					currentAccountId={accountId}
					lastPulseAt={page.latestRun?.createdAt}
					onContext={openContext}
				/>
			) : (
				<div className="mb-6"><AccountPicker currentAccountId={accountId} account={null} /></div>
			)}

			{accountId && page.account?.standing?.position && (
				<StandingCard account={page.account} compact className="mb-6" />
			)}

			{page.takeError && <Alert variant="destructive" className="mb-4"><AlertTitle>Couldn't start</AlertTitle><AlertDescription>{takeErrorMessage}</AlertDescription></Alert>}

			{page.state === 'no-account' && <Card><CardContent><EmptyState icon={HeartIcon} title="Pick an account to listen to" description="Choose a client and one of their accounts. We'll read how people reply to its recent posts." /></CardContent></Card>}

			{page.state === 'empty' && <Card><CardContent><EmptyState icon={HeartIcon} title="No pulse yet" description="Take the first pulse and watch the feelings come in. It usually takes a few minutes." action={takeButton('Take the first pulse')} /></CardContent></Card>}

			{page.state === 'listening' && (
				<div className="space-y-6">
					<ListeningState run={page.live.run || page.latestRun} />
					{page.timeline.length > 0 && <PulseTimeline points={page.timeline} />}
					{history}
				</div>
			)}

			{['ready', 'partial', 'failed'].includes(page.state) && (
				<div className="space-y-6">
					{page.state === 'partial' && <Alert><AlertTitle>Partial pulse</AlertTitle><AlertDescription>Some posts couldn't be read this time. What we did hear is below{page.lastTerminal?.replies?.failed ? '; you can retry the unread replies from the history.' : '.'}</AlertDescription></Alert>}
					{page.state === 'failed' && <Alert variant="destructive"><AlertTitle>That pulse didn't land</AlertTitle><AlertDescription>{endSentence(page.lastTerminal?.error) || "We couldn't read this account's replies."} Try again in a few minutes.</AlertDescription></Alert>}
					{page.state !== 'failed' && (
						<>
							<PulseTimeline points={page.timeline} loading={page.timelineLoading} />
							<div>
								<div className="mb-2 flex flex-wrap items-center justify-between gap-2">
									<span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Latest pulse</span>
									{page.lastTerminal && (
										<Link
											to={buildRoute(ROUTE_PATTERNS.HEARTBEAT_RUN, { accountId, runId: page.lastTerminal._id })}
											className="text-sm font-medium text-brand-600 hover:underline"
										>
											See which posts made people feel what →
										</Link>
									)}
								</div>
								{/* "Is anything on fire" before "how does the room feel". */}
								<FlaggedLane className="mb-4" totals={page.lastTerminal?.totals} onOpen={page.openVoices} />
								<StanceStrip className="mb-4" totals={page.lastTerminal?.totals} grid={page.grid} onOpen={page.openVoices} accountHandle={page.account?.handle} onContext={openContext} />
								<ContextReviewBanner className="mb-4" accountId={accountId} run={page.lastTerminal} onOpen={openContext} />
								<MoodTiles mood={page.lastTerminal?.mood} tiles={page.tiles} previousTiles={page.previousTiles} onOpen={page.openVoices} />
							</div>
						</>
					)}
					{history}
				</div>
			)}

			<EvidenceDrawer
				open={!!page.voices} onOpenChange={(o) => !o && page.closeVoices()}
				title={voicesTitle} description={page.voices?.text ? <PostLink url={page.voices?.url}>{page.voices.text}</PostLink> : undefined}
				filters={<FilterBar filters={replyFilters} />}
				items={page.replies} renderItem={(r) => <Reply r={r} accountId={accountId} runId={page.lastTerminal?._id} readingRevision={page.lastTerminal?.readingRevision} reviewActive={reviewIsActive(page.lastTerminal?.contextReview)} />}
				sortOptions={replySortOptions} sort={sort} onSortChange={setSort}
				loading={page.repliesLoading} hasMore={page.hasMoreReplies} onLoadMore={page.loadMoreReplies}
				emptyTitle="No replies match" emptyDescription="Try another standing, emotion, or post."
			/>
			<AccountContextSummary accountId={accountId} />
			<HeartbeatNotesDialog key={accountId} open={contextOpen} onOpenChange={setContextOpen} accountId={accountId} account={page.account} run={page.lastTerminal} grid={page.grid} />
		</main>
	);
}

// What the AI will use when it next reads this account. It was previously only
// reachable through the Context button, so a manager could not see what the model
// had been told without opening a dialog. Editing reuses that same dialog rather
// than growing a second editor with its own save path.
const CONTEXT_KIND_LABEL = { entity: 'Fact', event: 'Event', norm: 'How to judge', community: 'Audience' };

function AccountContextSummary({ accountId }) {
	const context = useHeartbeatContext(accountId, true);
	const notes = context.data?.notes || [];
	return <Card>
		<CardContent className="space-y-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<h2 className="text-sm font-semibold">What the AI knows about this account</h2>
				<Button asChild variant="outline" size="sm"><Link to={`${PATHS.HEARTBEAT_NOTES}?account=${encodeURIComponent(accountId)}`}>{notes.length ? 'Edit notes' : 'Add a note'}</Link></Button>
			</div>
			{context.isLoading && <p className="text-sm text-muted-foreground">Loading saved context...</p>}
			{context.isError && <p role="alert" className="text-sm text-rose-700">We couldn't load this account's context.</p>}
			{!context.isLoading && !context.isError && !notes.length && <p className="text-sm text-muted-foreground">Nothing saved yet. Add what the AI cannot guess about this account.</p>}
			{!!notes.length && <ul className="space-y-2">{notes.map((note) => <li key={note.id} className="rounded-lg border border-border p-3"><div className="flex items-start justify-between gap-3"><NoteSummary note={note} /><span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{CONTEXT_KIND_LABEL[note.kind] || 'Fact'}</span></div></li>)}</ul>}
		</CardContent>
	</Card>;
}
